import { orderStatusConstants } from "constants/order";
import {
  IHstImportBatchSummary,
  IHstImportBatchUser,
} from "types/HstImportBatch";

// Used by the import dialog's copy when the list endpoint has not told us the window.
export const DEFAULT_UNDO_WINDOW_DAYS = 7;

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** A date of service is a calendar date: never shift it through the local timezone. */
export const formatDateOfService = (value: string | null | undefined) => {
  if (!value) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

export const userDisplayName = (
  user: IHstImportBatchUser | string | null | undefined
): string => {
  if (!user) return "";
  if (typeof user === "string") return user;
  const name = [user.firstName, user.lastName]
    .filter((part) => part && part.trim())
    .join(" ")
    .trim();
  return name || user.email;
};

export const totalDuplicates = (batch: IHstImportBatchSummary) =>
  batch.duplicateCount + batch.csvDuplicateCount;

/** Client-side gate for the undo button; the server re-checks the name exactly. */
export const isAccountNameMatch = (typed: string, accountName: string) =>
  typed.trim().length > 0 &&
  typed.trim().toLowerCase() === accountName.trim().toLowerCase();

/** Same routing the estimate list uses: drafts open in review, everything else in the order view. */
export const orderPath = (
  orderId: string,
  orderStatus: string | null,
  accountId: string
) =>
  orderStatus === orderStatusConstants.DRAFT
    ? `/orders/review/${orderId}?accountId=${accountId}`
    : `/orders/${orderId}`;

/** RTK Query error → the server's message (and 409 blockers) for display. */
export const apiErrorMessage = (
  error: unknown,
  fallback = "Something went wrong. Please try again."
): { message: string; blockers: string[] } => {
  const data = (error as { data?: { message?: unknown; blockers?: unknown } })
    ?.data;
  const message =
    typeof data?.message === "string" && data.message.length > 0
      ? data.message
      : fallback;
  const rawBlockers: unknown = data?.blockers;
  const blockers = Array.isArray(rawBlockers)
    ? rawBlockers.filter((b): b is string => typeof b === "string")
    : [];
  return { message, blockers };
};

// Labels for the undo summary's per-table deleted counts (keys come from the API).
const DELETED_COUNT_LABELS: Record<string, string> = {
  order: "Estimates",
  patient: "Patients",
  gfeQuote: "Quotes",
  orderHistory: "Estimate history entries",
  providerPayout: "Provider payouts",
  paymentPlan: "Payment plans",
  paymentPlanBreakdown: "Payment plan breakdowns",
  paymentPlanRemaining: "Payment plan balances",
  pverifyResponse: "Eligibility checks",
  benefitObservation: "Benefit observations",
  benefitEnginePick: "Benefit engine picks",
};

export const deletedCountLabel = (key: string) =>
  DELETED_COUNT_LABELS[key] ||
  key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
